import {
  useInResiStore,
  useTrackingEvents,
  type Filters as OrmFilters,
  type TrackingEventWithUnits,
} from "@superbright/indexeddb-orm";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type CSSProperties,
} from "react";

import { type FiltersResponse } from "api/schemas";
import { FilterMultiSelectOptions } from "features/filters";
import { FilterButton } from "features/filters/components/FilterButton";
import styles from "features/filters/components/Filters.module.css";
import {
  distributeHighlightsToGroups,
  isTagFilterGroup,
  normalizeBodyKey,
} from "features/filters/dynamicFilters";
import {
  getCostLabel,
  getFilterBadge,
  getFilterDisplayLabel,
} from "features/filters/utils";
import { filterActions } from "store/actions";
import { type IFilters } from "types/filters";
import { useIsMobile } from "utils";

// Lazy-loaded so its JS+CSS chunk (FilterPrice.module.css) is fetched only
// when the slider filter is actually opened, instead of blocking initial paint.
const FilterPrice = lazy(() =>
  import("../../../features/filters/components/FilterPrice").then((m) => ({
    default: m.FilterPrice,
  }))
);
// useInResiStore is the canonical store hook; no separate usePropertyStore

// ---------- Types ----------
type FilterOption = {
  value?: string | number | string[];
  label?: string;
  min?: number;
  max?: number;
  key?: string;
  frequency?: number;
};

type SelectionItem = {
  value?: string | number;
  label?: string;
  index?: number;
};

type FilterDefBase = {
  name: string;
  label: string;
  component: "multiselect" | "slider";
  body_key?: string;
  helper_text?: string;
  options_variant?: string;
  min?: number;
  max?: number;
  options: FilterOption[];
};

type FilterDef = FilterDefBase & { index: number };

// ---------- Helpers ----------
// Map API filter names to store keys used by the ORM
const FILTER_STORE_KEY_MAP: Record<string, string> = {
  qty_bedrooms: "qty_bedrooms",
  date_availability: "date_availability",
  base_price: "base_price",
  highlights: "highlights",
};

function normalizeFilterKey(name: string): string {
  if (!name) return name;
  // normalize separators/case for lookup
  const normalized = name.replace(/[-\s]/g, "_");
  const direct = FILTER_STORE_KEY_MAP[normalized];
  if (direct) return direct;
  const lower = normalized.toLowerCase();
  return FILTER_STORE_KEY_MAP[lower] ?? normalized;
}
function toArray<T>(x: T | T[] | undefined | null): T[] {
  if (x == null) return [];
  return Array.isArray(x) ? x : [x];
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function readSliderNumber(
  name: string,
  temp: Record<string, unknown>,
  real: Record<string, unknown>,
  min: number,
  max: number
): number {
  const raw = temp[name] ?? real[name];
  const extracted =
    raw !== null && typeof raw === "object" && "value" in raw
      ? (raw as { value: unknown }).value
      : raw;
  const num = typeof extracted === "number" ? extracted : max; // default to max if unset
  return clamp(num, min, max);
}

function findIndexByValue(
  options: FilterOption[],
  value: string | number | undefined
): number {
  return options.findIndex((opt) => {
    const v = opt.value;
    return Array.isArray(v) ? v.includes(String(value ?? "")) : v === value;
  });
}

function selectionToValues(selection: unknown[]): (string | number)[] {
  return selection
    .flatMap((item): (string | number)[] => {
      if (item && typeof item === "object" && "value" in item) {
        const v = (item as SelectionItem).value;
        if (Array.isArray(v)) {
          return v.filter(
            (x): x is string | number =>
              typeof x === "string" || typeof x === "number"
          );
        }
        if (typeof v === "string" || typeof v === "number") return [v];
        return [];
      }
      if (typeof item === "string" || typeof item === "number") return [item];
      return [];
    })
    .filter((v): v is string | number => v !== undefined);
}

/**
 * Prefer the per-group store key; if empty (URL/bootstrap only restored the flat
 * `highlights` bag), derive this group's selection from tags that match its options.
 */
function resolveGroupSelection(
  storeKey: string,
  temp: Record<string, unknown>,
  real: Record<string, unknown>,
  options: FilterOption[],
  isTagGroup: boolean
): unknown[] {
  const optionValues = new Set(
    options.flatMap((o) => {
      const v = o.value ?? o.key;
      if (v == null) return [];
      return Array.isArray(v) ? v.map(String) : [String(v)];
    })
  );

  const direct = temp[storeKey] ?? real[storeKey];
  const directArr = toArray(direct);
  if (directArr.length > 0) {
    if (isTagGroup && optionValues.size > 0) {
      const owned = selectionToValues(directArr).filter((v) =>
        optionValues.has(String(v))
      );
      // Non-empty direct key may hold a stale/foreign bag (e.g. shared
      // `highlights`); fall through to the flat bag when nothing matches.
      if (owned.length > 0) return owned;
    } else {
      return directArr;
    }
  }

  if (!isTagGroup) return [];

  const flat = selectionToValues(
    toArray(temp.highlights ?? real.highlights)
  ).map(String);
  if (!flat.length || optionValues.size === 0) return [];
  return flat.filter((tag) => optionValues.has(tag));
}

function selectionToIndices(
  selection: unknown[],
  options: FilterOption[]
): number[] {
  return selection
    .map((item) => {
      if (item && typeof item === "object" && "index" in item) {
        return (item as SelectionItem).index ?? -1;
      }
      let value: string | number | undefined;
      if (item && typeof item === "object" && "value" in item) {
        value = (item as SelectionItem).value;
      } else if (typeof item === "string" || typeof item === "number") {
        value = item;
      } else {
        value = undefined;
      }
      return findIndexByValue(options, value);
    })
    .filter((i): i is number => i >= 0);
}

function normalizeSelectionForAnalytics(value: unknown): string[] {
  if (value == null) return [];
  const base = Array.isArray(value) ? (value as unknown[]) : [value];
  return base
    .flatMap((item: unknown) => {
      if (item == null) return [];
      if (Array.isArray(item)) return item as unknown[];
      if (typeof item === "object" && "value" in item) {
        const candidate = (item as { value: unknown }).value;
        return Array.isArray(candidate)
          ? (candidate as unknown[])
          : [candidate];
      }
      return [item];
    })
    .filter((item): item is string | number | boolean | object => item != null)
    .map((item) => String(item));
}

// ---------- Component ----------

interface FiltersProps {
  filtersData?: FiltersResponse | null;
  setTrackingEventWithUnits: (event: TrackingEventWithUnits | null) => void;
  propertySlug?: string;
}

export const Filters = ({
  filtersData,
  setTrackingEventWithUnits,
  propertySlug,
}: FiltersProps) => {
  const store = useInResiStore();
  const propertyId = useInResiStore((state) => state.currentPropertyId);
  const propertyName = propertySlug ?? "";

  const propertyNameForAnalytics = propertyName?.trim() || propertySlug || "";

  const analyticsContext = useMemo(
    () =>
      propertyId && propertySlug
        ? {
            propertyId: String(propertyId),
            propertySlug,
            propertyName: propertyNameForAnalytics,
          }
        : null,
    [propertyId, propertySlug, propertyNameForAnalytics]
  );

  const isMobile = useIsMobile();
  const { trackFilterOpened, trackFilterApplied, trackFilterCanceled } =
    useTrackingEvents();

  const [openFilter, setOpenFilter] = useState<FilterDef | null>(null);
  const [dropdownX, setDropdownX] = useState(0);
  const [alignment, setAlignment] = useState<"left" | "right">("left");
  const [contentMaxH, setContentMaxH] = useState<number | null>(null);
  const [initialTempFilters, setInitialTempFilters] = useState(
    store.tempFilters as unknown as IFilters
  );

  const buttonGroupRef = useRef<HTMLDivElement>(null);
  // dynamic refs keyed by filter name
  const filterRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Signature of the last flat `highlights` bag we distributed — re-run when it
  // changes (URL/bootstrap often arrive after /filters config).
  const lastDistributedHighlightsRef = useRef<string | null>(null);
  const committedFilters = useInResiStore((s) => s.filters);

  // When filters config arrives (or highlights hydrate later), spread the flat
  // `highlights` bag into per-group keys so Sunny / New highlights / etc. pills
  // restore their selection — and scrub foreign tags out of a group key named
  // `highlights` if other groups' tags were merged in.
  useEffect(() => {
    const map = filtersData?.data?.filters as
      | Record<string, FilterDefBase>
      | undefined;
    if (!map) return;

    const current = useInResiStore.getState().filters as Record<
      string,
      unknown
    >;
    const flat = Array.isArray(current.highlights)
      ? (current.highlights as unknown[])
          .map((t) => String(t))
          .sort()
          .join(",")
      : "";
    if (lastDistributedHighlightsRef.current === flat) return;

    const next = distributeHighlightsToGroups(current, map);
    const changed = Object.keys(next).some(
      (k) => JSON.stringify(next[k]) !== JSON.stringify(current[k])
    );
    if (changed) {
      filterActions.set(next as Partial<OrmFilters>);
    }
    lastDistributedHighlightsRef.current = flat;
  }, [filtersData, committedFilters]);

  const handleFilterClick = useCallback(
    (filter: FilterDef) => {
      if (openFilter && openFilter.name === filter.name) {
        setOpenFilter(null);
        return;
      }
      setInitialTempFilters(store.tempFilters as unknown as IFilters);
      setOpenFilter(filter);
      if (analyticsContext) {
        const key = normalizeFilterKey(filter.name);
        const storeFilters = store.tempFilters as Record<string, unknown>;
        const committedFilters = store.filters as Record<string, unknown>;
        const selectedValues = normalizeSelectionForAnalytics(
          storeFilters[key] ?? committedFilters[key] ?? []
        );
        trackFilterOpened({
          ...analyticsContext,
          filterIndex: filter.index ?? 0,
          filterLabel: filter.label,
          selectedFiltersEnum: selectedValues,
          filterName: filter.name,
        });
      }
    },
    [
      analyticsContext,
      openFilter,
      store.filters,
      store.tempFilters,
      trackFilterOpened,
    ]
  );

  const alignDropdown = useCallback(() => {
    if (!openFilter || !buttonGroupRef.current) return;

    const refKey = openFilter.name;
    const refEl = filterRefs.current[refKey];
    if (!refEl) return;

    const containerRect = buttonGroupRef.current.getBoundingClientRect();
    const offset = refEl.offsetLeft;
    const buttonWidth = refEl.offsetWidth;
    const dropdownWidth = 377;
    const viewportWidth = window.innerWidth;

    if (isMobile) {
      setDropdownX(-containerRect.left);
      setAlignment("left");
      const dropdownTop = containerRect.bottom + 17;
      const reservedForChrome = 140;
      const avail = window.innerHeight - dropdownTop - reservedForChrome;
      setContentMaxH(Math.max(140, Math.round(avail)));
      return;
    }

    setContentMaxH(null);

    const buttonLeft = containerRect.left + offset;
    const rightEdge = buttonLeft + dropdownWidth;
    if (rightEdge > viewportWidth) {
      setDropdownX(offset + buttonWidth - dropdownWidth);
      setAlignment("right");
    } else {
      setDropdownX(offset);
      setAlignment("left");
    }
  }, [openFilter, isMobile]);

  useEffect(() => {
    if (openFilter) alignDropdown();
    const onResize = () => openFilter && alignDropdown();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [openFilter, alignDropdown]);

  const closeFilter = useCallback(() => {
    if (openFilter) {
      if (
        JSON.stringify(store.tempFilters) !== JSON.stringify(initialTempFilters)
      ) {
        void store.setTempFilters(
          initialTempFilters as unknown as Parameters<
            typeof store.setTempFilters
          >[0]
        );
      }
    }
    setOpenFilter(null);
  }, [openFilter, store, initialTempFilters]);

  if (!filtersData) return null;

  const temp = store.tempFilters;
  const real = store.filters;

  return (
    <div className={styles.container} ref={buttonGroupRef}>
      <div className={styles.buttonGroupContainer}>
        <div className={styles.buttonGroup}>
          {Object.entries(filtersData?.data?.filters).map(
            ([_, value], index) => {
              const defBase = value as FilterDefBase;
              const def: FilterDef = { ...defBase, index };

              // prefer temp over real for selection
              const storeKey = normalizeFilterKey(def.name);
              const tagGroup = isTagFilterGroup(def);
              const selection = resolveGroupSelection(
                storeKey,
                temp as Record<string, unknown>,
                real as Record<string, unknown>,
                def.options,
                tagGroup
              );
              const selectedValues = selectionToValues(selection);

              const min = def.options?.[0]?.min ?? 0;
              const max = def.options?.[0]?.max ?? 15000;

              const label =
                def.component === "slider"
                  ? getCostLabel(
                      readSliderNumber(storeKey, temp, real, min, max),
                      min,
                      max,
                      isMobile
                    )
                  : getFilterDisplayLabel(
                      def.name,
                      selectedValues,
                      def.options as {
                        label: string;
                        value: string | number;
                      }[],
                      {
                        forceCountStyle: tagGroup,
                        // Tag groups use CMS label; structural pills keep short
                        // names (Bedrooms / Availability / Price) via name map.
                        displayLabel: tagGroup ? def.label : undefined,
                      }
                    );

              return (
                <FilterButton
                  key={def.name}
                  label={label}
                  active={openFilter !== null && openFilter.name === def.name}
                  onClick={() => handleFilterClick(def)}
                  refKey={def.name}
                  ref={(el: HTMLDivElement | null) => {
                    filterRefs.current[def.name] = el;
                  }}
                  badge={getFilterBadge(
                    def.name,
                    selectedValues,
                    filtersData?.data?.filters as unknown as Parameters<
                      typeof getFilterBadge
                    >[2],
                    { forceCountStyle: tagGroup }
                  )}
                />
              );
            }
          )}
        </div>
      </div>

      {/* Dropdown Panel */}
      <div
        className={styles.dropdownArea}
        data-open={!!openFilter}
        data-alignment={alignment}
        style={
          {
            transform: `translateX(${dropdownX}px)`,
            opacity: openFilter ? 1 : 0,
            pointerEvents: openFilter ? "auto" : "none",
            transition: "opacity 200ms ease",
            ...(contentMaxH != null
              ? { "--filter-content-max-h": `${contentMaxH}px` }
              : {}),
          } as CSSProperties
        }
      >
        {openFilter && (
          <div className={styles.dropdownPanel}>
            {openFilter.component === "multiselect" && (
              <div className={styles.fadeContent}>
                {(() => {
                  const name = normalizeFilterKey(openFilter.name);
                  const tagGroup = isTagFilterGroup(openFilter);
                  const selection = resolveGroupSelection(
                    name,
                    store.tempFilters as Record<string, unknown>,
                    store.filters as Record<string, unknown>,
                    openFilter.options,
                    tagGroup
                  );
                  const indices = selectionToIndices(
                    selection,
                    openFilter.options
                  );
                  return (
                    <FilterMultiSelectOptions
                      label={openFilter.label}
                      options={
                        openFilter.options as {
                          label: string;
                          value: string | string[];
                        }[]
                      }
                      onChange={(selectedIndices: number[]) => {
                        const bodyKey = normalizeBodyKey(
                          openFilter.body_key ?? openFilter.name
                        );
                        const newValue = selectedIndices.map((idx) => ({
                          index: idx,
                          bodyKey,
                          value: openFilter.options[idx]?.value,
                          label: openFilter.options[idx]?.label,
                        }));
                        void store.handleTempFilterChange(
                          name as keyof OrmFilters,
                          newValue as OrmFilters[keyof OrmFilters]
                        );
                      }}
                      onClear={() => {
                        const clearedValues =
                          (store.tempFilters as Record<string, unknown>)[
                            name
                          ] ?? [];
                        if (analyticsContext) {
                          const clearedSelection =
                            normalizeSelectionForAnalytics(clearedValues);
                          setTrackingEventWithUnits({
                            ...analyticsContext,
                            filterLabel: openFilter.label,
                            filterIndex: openFilter.index ?? 0,
                            selectedFiltersEnum: clearedSelection,
                            trackingEvent: trackFilterCanceled,
                            clearedFiltersEnum: clearedSelection,
                            clearedValues,
                          });
                        }
                        void store.handleTempFilterChange(
                          name as keyof OrmFilters,
                          [] as OrmFilters[keyof OrmFilters]
                        );
                        filterActions.set({
                          [name]: [],
                        } as Partial<OrmFilters>);
                        setInitialTempFilters((prev) => ({
                          ...prev,
                          [name]: [],
                        }));
                        setOpenFilter(null);
                      }}
                      onApply={() => {
                        const selNow =
                          (store.tempFilters as Record<string, unknown>)[
                            name
                          ] ?? [];
                        const valueArray = Array.isArray(selNow)
                          ? selNow
                          : [selNow];

                        const previousValues = (
                          store.filters as Record<string, unknown>
                        )[name];

                        filterActions.set({
                          [name]: valueArray,
                        } as Partial<OrmFilters>);
                        setInitialTempFilters((prev) => ({
                          ...prev,
                          [openFilter.name]: valueArray,
                        }));
                        setOpenFilter(null);
                        if (analyticsContext) {
                          const selectedValues =
                            normalizeSelectionForAnalytics(valueArray);
                          setTrackingEventWithUnits({
                            ...analyticsContext,
                            filterLabel: openFilter.label,
                            filterIndex: openFilter.index ?? 0,
                            selectedFiltersEnum: selectedValues,
                            trackingEvent: trackFilterApplied,
                            previousValues,
                          });
                        }
                      }}
                      // indices for selection UI
                      values={indices}
                      optionsVariant={
                        openFilter.options_variant === "wrap" ||
                        openFilter.options.length > 4
                          ? "wrap"
                          : "grid"
                      }
                      max={
                        openFilter.max != null && openFilter.max > 0
                          ? openFilter.max
                          : undefined
                      }
                      isQuestionnaire={false}
                      onDismiss={closeFilter}
                    />
                  );
                })()}
              </div>
            )}

            {openFilter.component === "slider" && (
              <div className={styles.fadeContent}>
                {(() => {
                  const name = normalizeFilterKey(openFilter.name);
                  const min = openFilter.options?.[0]?.min ?? 0;
                  const max = openFilter.options?.[0]?.max ?? 15000;
                  const current = readSliderNumber(
                    name,
                    store.tempFilters,
                    store.filters,
                    min,
                    max
                  );

                  return (
                    <Suspense fallback={null}>
                      <FilterPrice
                        min={min}
                        max={max}
                        value={current}
                        onChange={(v: number) => {
                          void store.handleTempFilterChange(
                            name as keyof OrmFilters,
                            clamp(v, min, max) as OrmFilters[keyof OrmFilters]
                          );
                        }}
                        onApply={() => {
                          const v = readSliderNumber(
                            name,
                            store.tempFilters,
                            store.filters,
                            min,
                            max
                          );

                          const previousValue = store.filters.base_price;

                          if (analyticsContext) {
                            const selectedValues =
                              normalizeSelectionForAnalytics(v);
                            setTrackingEventWithUnits({
                              ...analyticsContext,
                              filterLabel: openFilter.label,
                              filterIndex: openFilter.index ?? 0,
                              selectedFiltersEnum: selectedValues,
                              trackingEvent: trackFilterApplied,
                              previousValue,
                            });
                          }

                          filterActions.set({
                            [name]: v,
                          } as Partial<OrmFilters>);
                          setInitialTempFilters((prev) => ({
                            ...prev,
                            [name]: v,
                          }));
                          setOpenFilter(null);
                        }}
                        isQuestionnaire={false}
                        onDismiss={closeFilter}
                        onClear={() => {
                          // reset to max (or switch to undefined if you prefer "unset")
                          const v = max;
                          const prevValue = store.filters.base_price;
                          void store.handleTempFilterChange(
                            name as keyof OrmFilters,
                            v as OrmFilters[keyof OrmFilters]
                          );
                          filterActions.set({
                            [name]: v,
                          } as Partial<OrmFilters>);
                          setInitialTempFilters((prev) => ({
                            ...prev,
                            [name]: v,
                          }));
                          if (analyticsContext) {
                            const selectedValues =
                              normalizeSelectionForAnalytics(v);
                            setTrackingEventWithUnits({
                              ...analyticsContext,
                              filterLabel: openFilter.label,
                              filterIndex: openFilter.index ?? 0,
                              selectedFiltersEnum: selectedValues,
                              previousValue: prevValue,
                              trackingEvent: trackFilterCanceled,
                            });
                          }
                          setOpenFilter(null);
                        }}
                      />
                    </Suspense>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
